import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import api from '../../services/api';
import {
  BORDER_CITIES_BY_STATE,
} from '../../constants/constants';
import FeaturedCardsPreview from './FeaturedCardsPreview';
import { revalidatePublicPath } from '../../utils/revalidatePublic';
import './FeaturedCrossingsManager.css';

const EMPTY_COPY = {
  es: { eyebrow: '', title: '', description: '' },
  en: { eyebrow: '', title: '', description: '' },
};

const TABS = [
  { id: 'cards', label: 'Most active entries', icon: 'pi pi-th-large' },
  { id: 'copy', label: 'Section copy (ES / EN)', icon: 'pi pi-language' },
];

function newItemId() {
  return `fc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function emptyItem() {
  return {
    id: newItemId(),
    order: 0,
    city_code: '',
    city_label: '',
    state_code: '',
    use_fragment_port_reference: true,
    custom_city_label: '',
    entry_page_id: '',
    entry_page_slug: '',
    crossing_name: '',
    crossing_key: '',
  };
}

function crossingOptionsFromPage(page) {
  const list = page?.crossing_names_section?.crossings_names || [];
  return list
    .map((c, idx) => {
      const name = typeof c === 'string' ? c : c?.name || '';
      if (!name) return null;
      const key =
        (typeof c === 'object' && (c.urlFragment || c.key)) || name;
      return {
        name,
        key: String(key),
        label: name,
        index: idx,
      };
    })
    .filter(Boolean);
}

function cityMetaFromPage(page) {
  const cityCode = page?.departure?.city || page?.city || '';
  const stateCode = page?.departure?.state || page?.state || '';
  let cityLabel = '';
  if (cityCode && stateCode && BORDER_CITIES_BY_STATE[stateCode]) {
    cityLabel =
      BORDER_CITIES_BY_STATE[stateCode].find((c) => c.code === cityCode)?.name ||
      '';
  }
  if (!cityLabel && cityCode) cityLabel = cityCode;
  return { city_code: cityCode, city_label: cityLabel, state_code: stateCode };
}

/** Match entry pages to home lang (/es or /en). */
function pageMatchesLang(page, langKey) {
  const langUpper = String(langKey || '').toUpperCase();
  const pageLang = String(page?.lang || '').toUpperCase();
  if (pageLang && pageLang === langUpper) return true;

  const slug = String(page?.full_slug || page?.slug || '').toLowerCase();
  const prefix = `/${String(langKey || '').toLowerCase()}/`;
  if (slug.startsWith(prefix) || slug === `/${String(langKey || '').toLowerCase()}`) {
    return true;
  }
  return false;
}

function toPageOption(page) {
  const id = page?._id != null ? String(page._id) : '';
  const city = cityMetaFromPage(page);
  const cityBit = city.city_label ? ` · ${city.city_label}` : '';
  const slug = page?.full_slug || page?.slug || id;
  return {
    label: `${slug}${cityBit}`,
    value: id,
    page,
  };
}

function normalizeLoadedItems(raw) {
  const mapList = (list) =>
    (Array.isArray(list) ? list : []).map((item, index) => ({
      ...emptyItem(),
      ...item,
      id: item.id || newItemId(),
      order: item.order ?? index,
      entry_page_id: item.entry_page_id ? String(item.entry_page_id) : '',
      use_fragment_port_reference:
        item.use_fragment_port_reference === false ||
        item.use_fragment_port_reference === 'false' ||
        (item.use_fragment_port_reference == null &&
          (item.use_entry_page_departure_city === false ||
            item.use_entry_page_departure_city === 'false'))
          ? false
          : true,
      custom_city_label: item.custom_city_label || '',
    }));

  if (Array.isArray(raw)) {
    return { es: mapList(raw), en: [] };
  }
  return {
    es: mapList(raw?.es),
    en: mapList(raw?.en),
  };
}

function ActionBar({ onPreview, onSave, onPublish, saving, publishing }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.75rem',
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e5e7eb',
        width: '100%',
      }}
    >
      <Button
        type="button"
        label="Preview cards"
        icon="pi pi-eye"
        className="p-button-outlined"
        onClick={onPreview}
        style={{ width: 180, flex: '0 0 180px' }}
      />
      <Button
        type="button"
        label="Publish"
        icon="pi pi-send"
        className="p-button-warning"
        loading={publishing}
        disabled={saving}
        onClick={onPublish}
        style={{ width: 180, flex: '0 0 180px' }}
      />
      <Button
        type="button"
        label="Save"
        icon="pi pi-save"
        className="main-button"
        loading={saving}
        disabled={publishing}
        onClick={onSave}
        style={{ width: 180, flex: '0 0 180px' }}
      />
    </div>
  );
}

/** `#port-name` → `Port Name` */
function formatFragmentLabel(fragment) {
  const words = String(fragment || '')
    .replace(/^#/, '')
    .replace(/[^a-zA-Z0-9À-ÿ]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function parsedFragmentLabel(item, portOptions) {
  const selected =
    (portOptions || []).find((c) => c.name === item.crossing_name) ||
    (portOptions || []).find((c) => c.key === item.crossing_key);
  return formatFragmentLabel(selected?.key || item.crossing_key);
}

function cardCityLabel(item, portOptions) {
  if (item?.use_fragment_port_reference === false) {
    return String(item.custom_city_label || '').trim();
  }
  return parsedFragmentLabel(item, portOptions);
}

function LangCardsSection({
  langKey,
  title,
  hint,
  items,
  entryPages,
  onAdd,
  onRemove,
  onMove,
  onEntryPageChange,
  onCrossingChange,
  onChangeItem,
}) {
  const langPath = `/${langKey}`;
  const langOptions = [{ label: langPath, value: langKey }];

  const lastCardRef = useRef(null);
  const prevCountRef = useRef(items.length);

  const pageOptions = useMemo(() => {
    return (entryPages || [])
      .filter((p) => pageMatchesLang(p, langKey))
      .map(toPageOption)
      .filter((opt) => opt.value);
  }, [entryPages, langKey]);

  useEffect(() => {
    if (items.length > prevCountRef.current) {
      lastCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    prevCountRef.current = items.length;
  }, [items.length]);

  return (
    <Panel header={`${title} (${items.length})`} className="mb-4" toggleable>
      <Message
        severity="info"
        className="mb-3 w-full justify-content-start"
        text={hint}
      />

      <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <small className="text-600">
          {pageOptions.length
            ? `${pageOptions.length} entry page${pageOptions.length === 1 ? '' : 's'} for ${langPath}`
            : `No entry pages matched for ${langPath}${
                entryPages?.length ? ` (${entryPages.length} loaded total)` : ''
              }`}
        </small>
        <Button
          type="button"
          label="Add card"
          icon="pi pi-plus"
          onClick={onAdd}
        />
      </div>

      {items.length === 0 && (
        <Message
          severity="warn"
          className="mb-3 w-full justify-content-start"
          text={`No cards for /${langKey} yet. Add one to start.`}
        />
      )}

      {items.map((item, index) => {
        const selectedPage = entryPages.find(
          (p) => String(p._id) === String(item.entry_page_id)
        );
        const portOptions = crossingOptionsFromPage(selectedPage);
        const displayCity = cardCityLabel(item, portOptions);

        return (
          <div
            key={item.id}
            ref={index === items.length - 1 ? lastCardRef : null}
            style={{ scrollMarginTop: 16 }}
          >
          <Panel
            header={`Card ${index + 1}${
              item.crossing_name ? ` — ${item.crossing_name}` : ''
            }${displayCity ? ` · ${displayCity}` : ''}`}
            className="mb-3"
            toggleable
          >
            <div className="flex justify-content-end gap-1 mb-3">
              <Button
                type="button"
                icon="pi pi-arrow-up"
                className="p-button-text p-button-sm"
                disabled={index === 0}
                onClick={() => onMove(index, -1)}
              />
              <Button
                type="button"
                icon="pi pi-arrow-down"
                className="p-button-text p-button-sm"
                disabled={index === items.length - 1}
                onClick={() => onMove(index, 1)}
              />
              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-danger p-button-sm"
                onClick={() => onRemove(index)}
              />
            </div>

            <div className="grid">
              <div className="col-12 md:col-4">
                <label>
                  <strong>Language</strong>
                </label>
                <Dropdown
                  value={langKey}
                  options={langOptions}
                  optionLabel="label"
                  optionValue="value"
                  className="w-full"
                  disabled
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Entry page</strong>
                </label>
                <Dropdown
                  value={item.entry_page_id ? String(item.entry_page_id) : null}
                  options={pageOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={
                    pageOptions.length
                      ? 'Select entry page'
                      : `No ${langKey.toUpperCase()} entry pages`
                  }
                  className="w-full"
                  filter
                  filterBy="label"
                  filterInputAutoFocus
                  showClear
                  onChange={(e) => onEntryPageChange(index, e.value)}
                />
              </div>
              <div className="col-12 md:col-4">
                <label>
                  <strong>Port / crossing</strong>
                </label>
                <Dropdown
                  value={item.crossing_name || null}
                  options={portOptions}
                  optionLabel="label"
                  optionValue="name"
                  placeholder={
                    item.entry_page_id
                      ? portOptions.length
                        ? 'Select port'
                        : 'No crossings on this page'
                      : 'Select entry page first'
                  }
                  className="w-full"
                  disabled={!item.entry_page_id}
                  filter
                  filterInputAutoFocus
                  showClear
                  onChange={(e) => onCrossingChange(index, e.value)}
                />
              </div>
              <div className="col-12">
                <div className="flex align-items-center gap-2 mb-2">
                  <Checkbox
                    inputId={`use-fragment-${item.id}`}
                    checked={item.use_fragment_port_reference !== false}
                    onChange={(e) => {
                      const checked = Boolean(e.checked);
                      const patch = { use_fragment_port_reference: checked };
                      if (!checked) {
                        patch.custom_city_label = parsedFragmentLabel(
                          item,
                          portOptions
                        );
                      }
                      onChangeItem(index, patch);
                    }}
                  />
                  <label htmlFor={`use-fragment-${item.id}`} className="m-0">
                    Use Fragment Port Reference
                  </label>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <label>
                  <strong>City text</strong>
                </label>
                <InputText
                  className="w-full"
                  value={
                    item.use_fragment_port_reference !== false
                      ? parsedFragmentLabel(item, portOptions)
                      : item.custom_city_label || ''
                  }
                  disabled={item.use_fragment_port_reference !== false}
                  placeholder="Custom city text"
                  onChange={(e) =>
                    onChangeItem(index, { custom_city_label: e.target.value })
                  }
                />
              </div>
            </div>
          </Panel>
          </div>
        );
      })}
    </Panel>
  );
}

export default function FeaturedCrossingsManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewLang, setPreviewLang] = useState('es');
  const [entryPages, setEntryPages] = useState([]);
  const [copy, setCopy] = useState(EMPTY_COPY);
  const [itemsByLang, setItemsByLang] = useState({ es: [], en: [] });
  const [activeTab, setActiveTab] = useState('cards');
  const [publishing, setPublishing] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, pagesRes] = await Promise.all([
        api.get('/featured_crossings'),
        // translations includes `lang`; summary also has full_slug / crossings
        api.get('/entry_pages/summary').catch(() => null),
      ]);

      let pages = Array.isArray(pagesRes?.data) ? pagesRes.data : [];

      // Ensure lang is present (older workers omit it from summary)
      const missingLang = pages.length === 0 || pages.every((p) => !p.lang);
      if (missingLang) {
        try {
          const langRes = await api.get('/entry_pages/translations');
          const withLang = Array.isArray(langRes.data) ? langRes.data : [];
          if (pages.length === 0) {
            pages = withLang;
          } else {
            const langById = new Map(
              withLang.map((p) => [String(p._id), p.lang])
            );
            pages = pages.map((p) => ({
              ...p,
              lang: p.lang || langById.get(String(p._id)) || '',
            }));
          }
        } catch (e) {
          console.warn('Could not enrich entry pages with lang', e);
        }
      }

      const data = configRes.data || {};
      setCopy({
        es: { ...EMPTY_COPY.es, ...(data.copy?.es || {}) },
        en: { ...EMPTY_COPY.en, ...(data.copy?.en || {}) },
      });
      setItemsByLang(normalizeLoadedItems(data.items));
      setEntryPages(pages);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load featured crossings config',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateItem = (langKey, index, patch) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [langKey]: list };
    });
  };

  const handleEntryPageChange = (langKey, index, pageId) => {
    const page = entryPages.find((p) => String(p._id) === String(pageId));
    const crossings = crossingOptionsFromPage(page);
    const city = cityMetaFromPage(page);
    updateItem(langKey, index, {
      ...city,
      entry_page_id: pageId || '',
      entry_page_slug: page?.full_slug || '',
      crossing_name: crossings[0]?.name || '',
      crossing_key: crossings[0]?.key || '',
    });
  };

  const handleCrossingChange = (langKey, index, crossingName) => {
    const item = itemsByLang[langKey]?.[index];
    const page = entryPages.find(
      (p) => String(p._id) === String(item?.entry_page_id)
    );
    const crossings = crossingOptionsFromPage(page);
    const selected = crossings.find((c) => c.name === crossingName);
    updateItem(langKey, index, {
      crossing_name: crossingName || '',
      crossing_key: selected?.key || crossingName || '',
    });
  };

  const addItem = (langKey) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      list.push({ ...emptyItem(), order: list.length });
      return { ...prev, [langKey]: list };
    });
  };

  const removeItem = (langKey, index) => {
    setItemsByLang((prev) => ({
      ...prev,
      [langKey]: (prev[langKey] || [])
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, order: i })),
    }));
  };

  const moveItem = (langKey, index, dir) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      const target = index + dir;
      if (target < 0 || target >= list.length) return prev;
      const tmp = list[index];
      list[index] = list[target];
      list[target] = tmp;
      return {
        ...prev,
        [langKey]: list.map((item, i) => ({ ...item, order: i })),
      };
    });
  };

  const onSave = async ({ silent = false } = {}) => {
    const all = [...(itemsByLang.es || []), ...(itemsByLang.en || [])];
    const invalid = all.some((item) => {
      if (!item.entry_page_id || !item.crossing_name) return true;
      if (
        item.use_fragment_port_reference === false &&
        !String(item.custom_city_label || '').trim()
      ) {
        return true;
      }
      return false;
    });
    if (invalid) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Incomplete',
        detail:
          'Each card needs an entry page, a port/crossing, and a city label (port fragment or custom text).',
        life: 4000,
      });
      return false;
    }

    setSaving(true);
    try {
      await api.put('/featured_crossings', {
        copy,
        items: {
          es: (itemsByLang.es || []).map((item, index) => ({ ...item, order: index })),
          en: (itemsByLang.en || []).map((item, index) => ({ ...item, order: index })),
        },
      });

      if (!silent) {
        toast.current?.show({
          severity: 'success',
          summary: 'Saved',
          detail: 'Featured crossings saved to DB. Use Publish to refresh public pages.',
          life: 3500,
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'Save failed',
        life: 4000,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const revalidatePath = async (publicUrl, secret, path) => {
    return revalidatePublicPath(publicUrl, secret, path);
  };

  const onPublish = async () => {
    const saved = await onSave({ silent: true });
    if (!saved) return;

    const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';
    if (!publicUrl || !secret) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Saved, not published',
        detail: 'Missing REACT_APP_BORDERGARITA_PUBLIC_APP_URL or REVALIDATE_SECRET',
        life: 5000,
      });
      return;
    }

    setPublishing(true);
    try {
      let countrySlugs = [];
      try {
        const countryRes = await api.get('/country_pages/summary');
        countrySlugs = (Array.isArray(countryRes.data) ? countryRes.data : [])
          .map((p) => p.full_slug)
          .filter(Boolean);
      } catch (e) {
        console.warn('Could not load country pages for revalidate', e);
      }

      const paths = [
        '/es',
        '/en',
        ...countrySlugs,
        ...(countrySlugs.length
          ? []
          : ['/es/mexico', '/en/mexico', '/es/canada', '/en/canada']),
      ];
      const uniquePaths = [...new Set(paths)];

      const results = await Promise.allSettled(
        uniquePaths.map((path) => revalidatePath(publicUrl, secret, path))
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      toast.current?.show({
        severity: failed ? 'warn' : 'success',
        summary: 'Published',
        detail: failed
          ? `Saved + revalidated ${ok}/${uniquePaths.length} paths (${failed} failed)`
          : `Saved + revalidated ${ok} paths (/es, /en + country pages)`,
        life: 4500,
      });
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Publish error',
        detail: err?.message || 'Revalidation failed',
        life: 4000,
      });
    } finally {
      setPublishing(false);
    }
  };

  const updateCopy = (lang, field, value) => {
    setCopy((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  if (loading) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center"
        style={{ minHeight: 280 }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
        <div className="mt-3">Loading featured crossings…</div>
      </div>
    );
  }

  const previewItems =
    previewLang === 'en' ? itemsByLang.en || [] : itemsByLang.es || [];

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <Card
        title="Most active entries"
        subTitle="Cruces Destacados — separate cards for /es and /en homes"
      >
        <div className="fc-tabs" role="tablist" aria-label="Featured crossings sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`fc-tab${activeTab === tab.id ? ' fc-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={tab.icon} aria-hidden />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {activeTab === 'cards' && (
          <div className="fc-tab-panel" role="tabpanel">
            <LangCardsSection
              langKey="es"
              title="Español · /es"
              hint="Cards shown on the Spanish home. Pick an ES entry page and port. City text uses the port URL fragment unless you add custom text."
              items={itemsByLang.es || []}
              entryPages={entryPages}
              onAdd={() => addItem('es')}
              onRemove={(i) => removeItem('es', i)}
              onMove={(i, d) => moveItem('es', i, d)}
              onEntryPageChange={(i, v) => handleEntryPageChange('es', i, v)}
              onCrossingChange={(i, v) => handleCrossingChange('es', i, v)}
              onChangeItem={(i, patch) => updateItem('es', i, patch)}
            />

            <LangCardsSection
              langKey="en"
              title="English · /en"
              hint="Cards shown on the English home. Pick an EN entry page and port. City text uses the port URL fragment unless you add custom text."
              items={itemsByLang.en || []}
              entryPages={entryPages}
              onAdd={() => addItem('en')}
              onRemove={(i) => removeItem('en', i)}
              onMove={(i, d) => moveItem('en', i, d)}
              onEntryPageChange={(i, v) => handleEntryPageChange('en', i, v)}
              onCrossingChange={(i, v) => handleCrossingChange('en', i, v)}
              onChangeItem={(i, patch) => updateItem('en', i, patch)}
            />

            <ActionBar
              onPreview={() => setPreviewVisible(true)}
              onSave={onSave}
              onPublish={onPublish}
              saving={saving}
              publishing={publishing}
            />
          </div>
        )}

        {activeTab === 'copy' && (
          <div className="fc-tab-panel" role="tabpanel">
            <div className="grid">
              <div className="col-12 md:col-6">
                <Panel header="Español" className="h-full">
                  <div className="mb-3">
                    <label>
                      <strong>Eyebrow</strong>
                    </label>
                    <InputText
                      value={copy.es.eyebrow}
                      onChange={(e) => updateCopy('es', 'eyebrow', e.target.value)}
                      className="w-full"
                      placeholder="Section eyebrow"
                    />
                  </div>
                  <div className="mb-3">
                    <label>
                      <strong>Title</strong>
                    </label>
                    <InputText
                      value={copy.es.title}
                      onChange={(e) => updateCopy('es', 'title', e.target.value)}
                      className="w-full"
                      placeholder="Section title"
                    />
                  </div>
                  <div>
                    <label>
                      <strong>Description</strong>
                    </label>
                    <InputTextarea
                      value={copy.es.description}
                      onChange={(e) =>
                        updateCopy('es', 'description', e.target.value)
                      }
                      rows={4}
                      className="w-full"
                      autoResize
                      placeholder="Section description"
                    />
                  </div>
                </Panel>
              </div>
              <div className="col-12 md:col-6">
                <Panel header="English" className="h-full">
                  <div className="mb-3">
                    <label>
                      <strong>Eyebrow</strong>
                    </label>
                    <InputText
                      value={copy.en.eyebrow}
                      onChange={(e) => updateCopy('en', 'eyebrow', e.target.value)}
                      className="w-full"
                      placeholder="Section eyebrow"
                    />
                  </div>
                  <div className="mb-3">
                    <label>
                      <strong>Title</strong>
                    </label>
                    <InputText
                      value={copy.en.title}
                      onChange={(e) => updateCopy('en', 'title', e.target.value)}
                      className="w-full"
                      placeholder="Section title"
                    />
                  </div>
                  <div>
                    <label>
                      <strong>Description</strong>
                    </label>
                    <InputTextarea
                      value={copy.en.description}
                      onChange={(e) =>
                        updateCopy('en', 'description', e.target.value)
                      }
                      rows={4}
                      className="w-full"
                      autoResize
                      placeholder="Section description"
                    />
                  </div>
                </Panel>
              </div>
            </div>

            <ActionBar
              onPreview={() => setPreviewVisible(true)}
              onSave={onSave}
              onPublish={onPublish}
              saving={saving}
              publishing={publishing}
            />
          </div>
        )}
      </Card>

      <Dialog
        header="Featured cards preview"
        visible={previewVisible}
        onHide={() => setPreviewVisible(false)}
        style={{ width: '96vw', maxWidth: 1280 }}
        maximizable
        blockScroll
        dismissableMask
        contentStyle={{
          padding: 0,
          background: '#f3f4f6',
          maxHeight: '82vh',
          overflow: 'auto',
        }}
      >
        <div style={{ padding: '16px 20px 8px' }}>
          <Dropdown
            value={previewLang}
            options={[
              { label: 'Español (/es)', value: 'es' },
              { label: 'English (/en)', value: 'en' },
            ]}
            onChange={(e) => setPreviewLang(e.value)}
            className="mb-3"
            style={{ minWidth: 180 }}
          />
        </div>
        <FeaturedCardsPreview
          copy={copy[previewLang] || copy.es}
          items={previewItems}
          entryPages={entryPages}
          lang={previewLang}
        />
      </Dialog>
    </div>
  );
}
